import React, { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Button, Container, Form, Row, Col } from "react-bootstrap";
import { Autocomplete, TextField } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { InventoryAxios } from "api/InventoryAxios";
import {
  fetchCategories,
  fetchSubcategories,
  fetchMakes,
  fetchModels,
} from "slices/enquiry/thunk";
import { WarehouseAxios } from "api/WarehouseAxios";

// --- Types ---
type FormValues = {
  category: string;
  subCategory: string;
  make: string;
  model: string;
  qty: string;
  unit: string;
  unitPrice: string;
  total: string;
  productName: string;
  categoryName: string;
  subCategoryName: string;
  makeName: string;
  modelName: string;
};

interface ProductDetail {
  id: string | number;
  item_name: string;
  selling_price: number;
  model_name: string;
}

// --- Component ---
const ReorderLevelForm: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // --- Local State Arrays for Dropdowns ---
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [makes, setMakes] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);

  // --- Other State ---
  const [formData, setFormData] = useState<FormValues>({
    category: "",
    categoryName: "",
    subCategory: "",
    subCategoryName: "",
    make: "",
    makeName: "",
    model: "",
    modelName: "",
    qty: "",
    unit: "",
    unitPrice: "",
    total: "",
    productName: "",
  });
  const [productData, setProductData] = useState<ProductDetail | null>(null);
  const [errors, setErrors] = useState<Partial<FormValues>>({});
  const [items, setItems] = useState<FormValues[]>([]);

  // --- Fetch Categories on Mount ---
  useEffect(() => {
    dispatch(fetchCategories("products")).then((res: any) => {
      if (res?.payload) setCategories(res.payload);
    });
  }, [dispatch]);

  // --- Handlers ---
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleModelChange = (_: any, newValue: any) => {
    setProductData(newValue);

    setFormData((prev) => ({
      ...prev,
      model: newValue ? newValue.id : "",
      modelName: newValue ? newValue.model_name : "",
      unitPrice: newValue?.selling_price?.toString() || "",
      productName: newValue?.item_name || "",
    }));
  };

  const validateForm = (values: FormValues) => {
    const newErrors: Partial<FormValues> = {};
    if (!values.category) newErrors.category = "Category is required";
    if (!values.subCategory) newErrors.subCategory = "Sub-category is required";
    if (!values.make) newErrors.make = "Make is required";
    if (!values.model) newErrors.model = "Model is required";
    if (!values.qty || Number(values.qty) <= 0) {
      newErrors.qty = "Quantity must be greater than 0";
    }
    // if (!values.unitPrice || Number(values.unitPrice) <= 0) {
    //   newErrors.unitPrice = "Unit Price must be greater than 0";
    // }
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    // e.preventDefault();

    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const qty = Number(formData.qty) || 0;
    const unitPrice = Number(formData.unitPrice) || 0;
    const total = (qty * unitPrice).toFixed(2);

    const newItem: FormValues = {
      ...formData,
      total,
      productName: productData?.item_name || formData.productName,
      modelName: productData?.model_name || formData.modelName,
      unitPrice: unitPrice.toString(),
    };

    setItems((prev) => [...prev, newItem]);
    setFormData({
      category: "",
      categoryName: "",
      subCategory: "",
      subCategoryName: "",
      make: "",
      makeName: "",
      model: "",
      modelName: "",
      qty: "",
      unit: "",
      unitPrice: "",
      total: "",
      productName: "",
    });
    setProductData(null);
  };

  const handlSubmitReordel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Please add at least one item before submitting");
      return;
    }

    // Prepare payload - transform items array to match API expectations
    const payload = {
      items: items.map((item) => ({
        category: item.category,
        category_name: item.categoryName,
        sub_category: item.subCategory,
        sub_category_name: item.subCategoryName,
        make: item.make,
        make_name: item.makeName,
        model: item.model,
        model_name: item.modelName,
        qty: item.qty,
        // unit: item.unit || "",
        // unit_name: item.unitName,
        // unit_price: item.unitPrice,
        // total: item.total,
        product_name: item.productName,
      })),
    };

    try {
      const response = await WarehouseAxios.post(
        "inventory/reorder_level/create/",
        payload
      );
      toast.success("Reorder Level Created Successfully");
      navigate("/inventory-reorder-level");
    } catch (error: any) {
      const status = error?.response?.status;
      const backendMessage =
        error?.response?.data?.msg || error?.response?.data?.message;
      const errType = error?.response?.data?.errors;

      if (backendMessage) {
        toast.error(backendMessage);
      } else if (errType && Object.keys(errType).length) {
        Object.keys(errType).forEach((key) => {
          toast.error(`${key}: ${errType[key]}`);
        });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      console.error("Error creating reorder level:", error);
    }
  };
  const handleDelete = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Calculate Display Total ---
  const displayTotal = useMemo(() => {
    const qty = Number(formData.qty) || 0;
    const unitPrice = Number(formData.unitPrice) || 0;
    return (qty * unitPrice).toFixed(2);
  }, [formData.qty, formData.unitPrice]);

  return (
    <div
      className="page-content px-4"
      style={{ backgroundColor: "#e9ecef", minHeight: "100vh" }}
    >
      <Container
        fluid
        style={{
          backgroundColor: "#fff",
          padding: "30px",
          borderRadius: "10px",
          boxShadow: "0 0 15px rgba(0,0,0,0.1)",
        }}
      >
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mb-4">
          {/* Left: Back Icon and Heading */}
          <div className="d-flex align-items-center gap-2">
            <Link
              to="/inventory-reorder-level"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              <i
                className="bi bi-arrow-left"
                style={{ fontSize: "1.2rem", cursor: "pointer" }}
                aria-label="Back to Reorder Level"
              />
            </Link>
            <h6 className="card-title mb-0">Create Reorder Level</h6>
          </div>

          {/* Right: Breadcrumb */}
          <div className="text-muted">
            <Link
              to="/inventory-reorder-level"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              Reorder Level
            </Link>{" "}
            &gt;{" "}
            <Link to="#" style={{ color: "inherit", textDecoration: "none" }}>
              Create Reorder Level
            </Link>
          </div>
        </div>

        <Form>
          <div className="row g-3 mb-3">
            {/* Category */}
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={categories}
                  getOptionLabel={(opt: any) => opt.name || ""}
                  value={
                    categories.find((c) => c.id === formData.category) || null
                  }
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      category: newValue ? newValue.id : "",
                      categoryName: newValue ? newValue.name : "",
                      subCategory: "",
                      subCategoryName: "",
                      make: "",
                      makeName: "",
                      model: "",
                      modelName: "",
                    }));
                    if (newValue) {
                      dispatch(
                        fetchSubcategories({
                          item_type: "products",
                          categoryId: newValue.id,
                        })
                      )
                        .then((res: any) => {
                          const payload = res?.payload || res?.data || [];
                          setSubcategories(
                            Array.isArray(payload) ? payload : []
                          );
                          setMakes([]);
                          setModels([]);
                        })
                        .catch((err: any) => {
                          console.error("Error fetching subcategories:", err);
                          setSubcategories([]);
                          setMakes([]);
                          setModels([]);
                        });
                    } else {
                      setSubcategories([]);
                      setMakes([]);
                      setModels([]);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select Category"
                    />
                  )}
                />
                {errors.category && (
                  <div className="text-danger small">{errors.category}</div>
                )}
              </Form.Group>
            </div>

            {/* Subcategory */}
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>
                  Sub-category <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={Array.isArray(subcategories) ? subcategories : []}
                  getOptionLabel={(opt: any) =>
                    opt.name || opt.subcategory_name || ""
                  }
                  value={
                    (Array.isArray(subcategories) &&
                      subcategories.find(
                        (s) =>
                          s.id === formData.subCategory ||
                          s.subcategory_id === formData.subCategory
                      )) ||
                    null
                  }
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      subCategory: newValue
                        ? newValue.id || newValue.subcategory_id || ""
                        : "",
                      subCategoryName: newValue
                        ? newValue.name || newValue.subcategory_name || ""
                        : "",
                      make: "",
                      makeName: "",
                      model: "",
                      modelName: "",
                    }));
                    if (newValue && formData.category) {
                      dispatch(
                        fetchMakes({
                          categoryId: formData.category,
                          subcategoryId: newValue.id,
                        })
                      )
                        .then((res: any) => {
                          const payload = res?.payload || res?.data || [];
                          setMakes(Array.isArray(payload) ? payload : []);
                          setModels([]);
                        })
                        .catch((err: any) => {
                          console.error("Error fetching makes:", err);
                          setMakes([]);
                          setModels([]);
                        });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select Sub-category"
                    />
                  )}
                  disabled={!formData.category}
                />
                {errors.subCategory && (
                  <div className="text-danger small">{errors.subCategory}</div>
                )}
              </Form.Group>
            </div>

            {/* Make */}
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>
                  Make <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={Array.isArray(makes) ? makes : []}
                  getOptionLabel={(opt: any) => opt.make_name || ""}
                  value={
                    (Array.isArray(makes) &&
                      makes.find((m) => m.id === formData.make)) ||
                    null
                  }
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      make: newValue ? newValue.id : "",
                      makeName: newValue ? newValue.make_name : "",
                      model: "",
                      modelName: "",
                    }));
                    if (newValue && formData.category && formData.subCategory) {
                      dispatch(
                        fetchModels({
                          categoryId: formData.category,
                          subcategoryId: formData.subCategory,
                          makeId: newValue.id,
                        })
                      )
                        .then((res: any) => {
                          const payload = res?.payload || res?.data || [];
                          setModels(Array.isArray(payload) ? payload : []);
                        })
                        .catch((err: any) => {
                          console.error("Error fetching models:", err);
                          setModels([]);
                        });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select Make"
                    />
                  )}
                  disabled={!formData.subCategory}
                />
                {errors.make && (
                  <div className="text-danger small">{errors.make}</div>
                )}
              </Form.Group>
            </div>

            {/* Model */}
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>
                  Model <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={Array.isArray(models) ? models : []}
                  getOptionLabel={(opt: any) => opt.model_name || ""}
                  value={productData}
                  onChange={handleModelChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select Model"
                    />
                  )}
                  disabled={!formData.make}
                />
                {errors.model && (
                  <div className="text-danger small">{errors.model}</div>
                )}
              </Form.Group>
            </div>
          </div>

          {/* Qty, Price, Total */}
          <div className="row g-3 mb-3">
            <div className="col-md-2">
              <Form.Group>
                <Form.Label>
                  Qty <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="qty"
                  value={formData.qty}
                  onChange={handleInputChange}
                  isInvalid={!!errors.qty}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.qty}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            {/* <div className="col-md-3">
              <Form.Group>
                <Form.Label>Unit Price <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  name="unitPrice"
                  value={formData.unitPrice}
                  onChange={handleInputChange}
                  isInvalid={!!errors.unitPrice}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.unitPrice}
                </Form.Control.Feedback>
              </Form.Group>
            </div> */}
            {/* <div className="col-md-3">
              <Form.Group>
                <Form.Label>Total</Form.Label>
                <Form.Control
                  type="number"
                  name="total"
                  value={displayTotal}
                  readOnly
                />
              </Form.Group>
            </div> */}
            <div className="col-md-2 d-flex align-items-end">
              <Button
                type="button"
                onClick={handleSubmit}
                className="btn btn-primary w-100"
              >
                + Add
              </Button>
            </div>
          </div>
        </Form>

        {/* Items Table */}
        <div className="table-responsive mb-3">
          <table className="table table-bordered align-middle">
            <thead>
              <tr>
                <th>Sr No.</th>
                <th>Product Name</th>
                <th>Category</th>
                <th>Sub-Category</th>
                <th>Make</th>
                <th>Model</th>
                <th>Qty</th>
                {/* <th>Unit Price</th> */}
                {/* <th>Total</th> */}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.productName}</td>
                    <td>{item.categoryName}</td>
                    <td>{item.subCategoryName}</td>
                    <td>{item.makeName}</td>
                    <td>{item.modelName}</td>
                    <td>{item.qty}</td>
                    {/* <td>{item.unitPrice}</td> */}
                    {/* <td>{item.total}</td> */}
                    <td>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(index)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center py-5 text-muted">
                    Search existing products to add to this list or add new
                    products to get started!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Submit and Cancel Buttons */}
        <div className="d-flex justify-content-center gap-3 py-3">
          <Button
            variant="light"
            onClick={() => navigate("/inventory-reorder-level")}
            style={{
              border: "1px solid #ced4da",
              backgroundColor: "white",
            }}
          >
            <span className="text-decoration-none" style={{ color: "#495057" }}>
              Cancel
            </span>
          </Button>

          <Button
            type="button"
            onClick={handlSubmitReordel}
            style={{
              background: "#3F7DDA",
              color: "#FFF",
            }}
            disabled={items.length === 0}
          >
            Submit
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default ReorderLevelForm;
